import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faChevronRight, 
  faCreditCard, 
  faCalendarAlt, 
  faLock,
  faUser,
  faMapMarkerAlt,
  faPhone,
  faEnvelope,
  faTruck,
  faCheck,
  faClock,
  faTimes,
  faTag,
  faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';
import { useCart } from '../../context/CartContext';
import { useLocation as useServiceLocation } from '../../context/LocationContext';
import { Elements } from '@stripe/react-stripe-js';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { 
  createPaymentIntent,
  processPayment,
  extractPriceNumeric,
  getStripe
} from '../../services/stripeService';
import { validateCoupon, calculateDiscount, applyCoupon, formatCouponValue } from '../../utils/couponUtils';
import { getActiveLocations } from '../../services/locationService';
import { calculateLocationDeliveryFee, OFFICE_LOCATIONS, DELIVERY_FEE_CONFIG } from '../../services/distanceService';
import { auth } from '../../firebase';
import '../../styles/checkout.css';
import '../../styles/ordering-paused.css';
import PlaceholderImage from '../common/PlaceholderImage';
import TermsModal from './TermsModal';
import { formatDateSafe } from '../../utils/dateUtils';

// Stripe Card Element styles
const cardElementOptions = {
  style: {
    base: {
      color: '#000',
      fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
      fontSmoothing: 'antialiased',
      fontSize: '16px',
      '::placeholder': {
        color: '#aab7c4'
      }
    },
    invalid: {
      color: '#fa755a',
      iconColor: '#fa755a'
    }
  },
  hidePostalCode: true
};

// Stripe Payment Form Component
const StripePaymentForm = ({ paymentInfo, handlePaymentInfoChange, errors, onSubmit }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [cardError, setCardError] = useState('');
  const [processing, setProcessing] = useState(false);

  const handleCardChange = (event) => {
    setCardError(event.error ? event.error.message : '');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) {
      return;
    }
    setProcessing(true);
    const cardElement = elements.getElement(CardElement);
    // Create payment method
    const { error, paymentMethod } = await stripe.createPaymentMethod({
      type: 'card',
      card: cardElement,
      billing_details: {
        name: paymentInfo.cardHolder,
      },
    });
    console.log('[StripePaymentForm] Created paymentMethod:', paymentMethod, 'Error:', error);
    if (error) {
      setCardError(error.message);
      setProcessing(false);
      return;
    }
    // Pass the payment method to the parent component
    onSubmit(paymentMethod);
    setProcessing(false);
  };

  return (
    <form className="stripe-payment-form" onSubmit={handleSubmit} autoComplete="off">
      <div className="form-group full-width">
        <label htmlFor="cardHolder">
          <FontAwesomeIcon icon={faUser} /> Card Holder Name
        </label>
        <input
          type="text"
          id="cardHolder"
          name="cardHolder"
          value={paymentInfo.cardHolder}
          onChange={handlePaymentInfoChange}
          placeholder="Name on card"
        />
        {errors.cardHolder && <div className="input-error">{errors.cardHolder}</div>}
      </div>
      
      <div className="form-group full-width">
        <label htmlFor="card-element">
          <FontAwesomeIcon icon={faCreditCard} /> Card Details
        </label>
        <div className="card-element-container">
          <CardElement id="card-element" options={cardElementOptions} onChange={handleCardChange} />
          <div className="card-security">
            <FontAwesomeIcon icon={faLock} /> Secure payment
          </div>
        </div>
        {cardError && <div className="input-error">{cardError}</div>}
      </div>
      
      <div className="form-group">
        <div className="checkbox-container">
          <input
            type="checkbox"
            id="savePaymentInfo"
            name="savePaymentInfo"
            checked={paymentInfo.savePaymentInfo}
            onChange={handlePaymentInfoChange}
          />
          <label htmlFor="savePaymentInfo">Save payment information for future orders</label>
        </div>
      </div>
      <button
        type="submit"
        className="btn btn-primary stripe-submit-btn"
        disabled={processing}
        style={{
          width: '100%',
          background: '#fff',
          color: '#111',
          border: '2px solid #111',
          fontWeight: 700,
          fontSize: '1.1rem',
          padding: '0.9em',
          marginTop: '1.2em',
          cursor: processing ? 'not-allowed' : 'pointer',
        }}
      >
        {processing ? 'Processing...' : 'Continue'}
      </button>
    </form>
  );
};

const CheckoutPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { cart, clearCart, addToCart } = useCart();
  // Per-location ordering kill switch (admin dashboard → Online Ordering)
  const { isOrderingPaused, getOrderingPauseMessage } = useServiceLocation();
  const [isGuest, setIsGuest] = useState(false);
  const [activeStep, setActiveStep] = useState(1);
  const [orderComplete, setOrderComplete] = useState(false);
  const [orderId, setOrderId] = useState('');
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentError, setPaymentError] = useState('');
  const [paymentStatus, setPaymentStatus] = useState('');
  const [deliveryFee, setDeliveryFee] = useState(50); // Default delivery fee
  const [selectedBranch, setSelectedBranch] = useState('');
  const [mileageDistance, setMileageDistance] = useState(0);
  const [feeCalculated, setFeeCalculated] = useState(false); // Track if delivery fee has been calculated
  const [deliveryRangeError, setDeliveryRangeError] = useState(null); // Blocks checkout when delivery is outside the unit's location radius
  const [showTermsModal, setShowTermsModal] = useState(false);
  
  // Payment option state - 'full' or 'deposit'
  const [paymentOption, setPaymentOption] = useState('deposit'); // Default to deposit since it's most common
  
  // Outlet and generator upsell state
  const [outletWithin100Feet, setOutletWithin100Feet] = useState(null); // null = not answered, true = yes, false = no
  const [showGeneratorUpsell, setShowGeneratorUpsell] = useState(false);
  const [generatorAdded, setGeneratorAdded] = useState(false);
  
  // Tip state
  const [tipAmount, setTipAmount] = useState(0);
  const [tipPercentage, setTipPercentage] = useState(0);
  const [customTipAmount, setCustomTipAmount] = useState('');
  
  // Coupon state
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponDiscount, setCouponDiscount] = useState(0);
  const [couponError, setCouponError] = useState('');
  const [couponLoading, setCouponLoading] = useState(false);
  
  // Dynamic service areas loaded from database
  const [serviceAreas, setServiceAreas] = useState([]);
  const [locationsLoading, setLocationsLoading] = useState(true);
  const [locationsError, setLocationsError] = useState(null);
  
  // Branch locations
  const branchLocations = {
    angola: {
      address: '15 Kodak Ln',
      city: 'Angola',
      state: 'IN',
      zip: '46703'
    },
    elkhart: {
      address: '56551 Mars Dr',
      city: 'Elkhart',
      state: 'IN',
      zip: '46516'
    }
  };

  // Load service areas from database
  useEffect(() => {
    const loadServiceAreas = async () => {
      try {
        setLocationsLoading(true);
        setLocationsError(null);
        const locations = await getActiveLocations();
        
        // Transform database locations to match the expected format
        const transformedLocations = locations.map(location => ({
          city: location.city,
          state: location.state,
          zipCodes: location.zipCodes || [],
          deliveryFee: location.deliveryFee || 50,
          notes: location.notes
        }));
        
        setServiceAreas(transformedLocations);
      } catch (error) {
        console.error('Error loading service areas:', error);
        setLocationsError('Failed to load service areas. Using default locations.');
        
        // Fallback to a minimal set of locations
        setServiceAreas([
          { city: 'Angola', state: 'IN', zipCodes: ['46703'], deliveryFee: 50 },
          { city: 'Fort Wayne', state: 'IN', zipCodes: ['46804', '46805'], deliveryFee: 50 }
        ]);
      } finally {
        setLocationsLoading(false);
      }
    };

    loadServiceAreas();
  }, []);

  // Map a product's stored location ("Angola" / "Elkhart" / "Both") to a
  // delivery office key. Returns null when there is no constraint (e.g. "Both"
  // items, add-ons, or products without a location).
  const productLocationToOffice = (loc) => {
    if (!loc) return null;
    const v = loc.toString().trim().toLowerCase();
    if (v === 'angola') return 'angola';
    if (v === 'elkhart') return 'elkhart';
    return null; // "Both" or unknown => either office can fulfill it
  };

  // Determine which office must fulfill the current cart based on its items'
  // locations. A unit physically lives at one warehouse, so the delivery
  // address must be within range of THAT office — not just the nearest one.
  const getCartFulfillmentOffice = () => {
    const offices = new Set();
    (cart.items || []).forEach(item => {
      const office = productLocationToOffice(item.location);
      if (office) offices.add(office);
    });

    // Items split across both warehouses can't ship from a single location.
    if (offices.size > 1) {
      return { office: null, mixed: true };
    }
    return { office: offices.values().next().value || null, mixed: false };
  };

  /**
   * Ordering can be paused per location from the admin dashboard. If the
   * warehouse that would fulfill this cart is paused, checkout is a hard stop —
   * this is the last gate before we take money for a booking we can't staff.
   * Returns null when ordering is open.
   */
  const getCartOrderingPause = () => {
    const { office, mixed } = getCartFulfillmentOffice();

    // Mixed carts already have their own blocking error.
    if (mixed) return null;

    const area =
      office === 'angola' ? 'Angola' : office === 'elkhart' ? 'Elkhart' : null;

    // A cart of "Both"/add-on-only items can ship from either warehouse, so it's
    // only blocked when every location is paused (handled by isOrderingPaused).
    const key = area || 'Both';
    if (!isOrderingPaused(key)) return null;

    return { area, message: getOrderingPauseMessage(key) };
  };

  const orderingPause = getCartOrderingPause();

  // Delivery fee calculation based on distance using Haversine formula.
  // Enforces that the delivery address is within the radius of the office that
  // actually stocks the cart's units.
  const calculateDeliveryFee = async (city, zipCode) => {
    if (!city || !zipCode) {
      setMileageDistance(0);
      setFeeCalculated(false);
      return 0;
    }

    const { office: assignedOffice, mixed } = getCartFulfillmentOffice();

    // A cart that mixes Angola-only and Elkhart-only units can't be delivered
    // from one warehouse — block it before we even calculate a fee.
    if (mixed) {
      setMileageDistance(0);
      setFeeCalculated(false);
      setDeliveryRangeError(
        'Your cart contains units from both our Angola and Elkhart locations. ' +
        'Please place a separate order for each location so we can deliver each item from the correct warehouse.'
      );
      return 0;
    }

    try {
      // Measure distance from the office that stocks these units (when known),
      // otherwise fall back to the nearest office for "Both"/add-on-only carts.
      const calculation = await calculateLocationDeliveryFee(city, 'IN', zipCode, assignedOffice);

      // Set distance and calculation status
      setMileageDistance(calculation.distance);
      setFeeCalculated(true);
      setDeliveryRangeError(null);
      setErrors(prev => ({ ...prev, deliveryRange: '' }));

      return calculation.fee;
    } catch (error) {
      console.error('Error calculating delivery fee:', error);

      // Out-of-range deliveries are a hard stop — never silently fall back to a
      // flat fee, or units end up booked at addresses we can't service.
      if (/delivery range|exceeds/i.test(error.message || '')) {
        const officeName = assignedOffice
          ? (OFFICE_LOCATIONS[assignedOffice]?.name || assignedOffice)
          : null;
        setMileageDistance(0);
        setFeeCalculated(false);
        setDeliveryRangeError(
          officeName
            ? `Sorry, ${city} is outside the ${DELIVERY_FEE_CONFIG.maxDeliveryDistance}-mile delivery radius for our ${officeName}. ` +
              `The unit(s) in your cart are only available from that location, so we're unable to deliver to this address.`
            : `Sorry, ${city} is outside our ${DELIVERY_FEE_CONFIG.maxDeliveryDistance}-mile delivery radius, so we're unable to deliver to this address.`
        );
        return 0;
      }

      // We couldn't measure the distance (geocoder unavailable). If this is an
      // admin-configured service area, trust that allowlist and use its set
      // delivery fee so legitimate local orders aren't blocked by a geo hiccup.
      const area = serviceAreas.find(area =>
        area.city.toLowerCase() === city.toLowerCase() &&
        (area.zipCodes.length === 0 || area.zipCodes.includes(zipCode))
      );

      if (area) {
        setDeliveryRangeError(null);
        setErrors(prev => ({ ...prev, deliveryRange: '' }));
        setMileageDistance(0); // Unknown distance
        setFeeCalculated(true);
        return area.deliveryFee;
      }

      // Not a known service area and we can't verify the distance — fail
      // CLOSED rather than guess a flat fee and risk an undeliverable booking.
      setMileageDistance(0);
      setFeeCalculated(false);
      setDeliveryRangeError(
        `We couldn't verify delivery to ${city}${zipCode ? ' ' + zipCode : ''}. ` +
        `Please double-check your address, or call us at (260) 226-5640 to arrange this delivery.`
      );
      return 0;
    }
  };
  
  // Form states
  const [customerInfo, setCustomerInfo] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: ''
  });
  
  const [deliveryInfo, setDeliveryInfo] = useState({
    address: '',
    city: '',
    state: '',
    zipCode: '',
    deliveryDate: '',
    deliveryTime: '',
    deliveryInstructions: '',
    // Add rental period fields
    rentalStartDate: '',
    rentalEndDate: '',
    rentalStartTime: '',
    rentalEndTime: ''
  });
  
  const [paymentInfo, setPaymentInfo] = useState({
    cardHolder: '',
    cardNumber: '',
    expMonth: '',
    expYear: '',
    cvv: '',
    savePaymentInfo: false
  });
  
  const [errors, setErrors] = useState({});
  
  // Add new state for liability waiver
  const [liabilityWaiver, setLiabilityWaiver] = useState({
    termsAgreed: false,
    purchaseDamageWaiver: false,
    surfaceType: '',
    powerWithin50Feet: false,
    overnightUpcharge: false
  });

  // Add handler for liability waiver inputs
  const handleLiabilityWaiverChange = (e) => {
    const { name, value, checked, type } = e.target;
    setLiabilityWaiver({
      ...liabilityWaiver,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  // Tip handler functions
  const handleTipPercentageChange = (percentage) => {
    setTipPercentage(percentage);
    setCustomTipAmount('');
    const calculatedTip = (cart.total * percentage) / 100;
    setTipAmount(calculatedTip);
  };

  const handleCustomTipChange = (e) => {
    const value = e.target.value;
    setCustomTipAmount(value);
    setTipPercentage(0);
    const tipValue = parseFloat(value) || 0;
    setTipAmount(tipValue);
  };

  // Payment option handler
  const handlePaymentOptionChange = (option) => {
    setPaymentOption(option);
  };

  // Helper functions for payment calculations
  const calculateFullTotal = () => {
    const damageWaiverAmount = liabilityWaiver.purchaseDamageWaiver ? cart.total * 0.1 : 0;
    const overnightUpchargeAmount = liabilityWaiver.overnightUpcharge ? 100 : 0;
    const subtotalWithExtras = cart.total + damageWaiverAmount + overnightUpchargeAmount + (feeCalculated ? deliveryFee : 50) + (cart.total * 0.07) + tipAmount;
    return Math.max(0, subtotalWithExtras - couponDiscount);
  };

  const calculateTax = () => {
    // Calculate tax on all taxable components: cart total + damage waiver + delivery fee
    const subtotal = cart.total;
    const damageWaiverAmount = liabilityWaiver.purchaseDamageWaiver ? cart.total * 0.1 : 0;
    const deliveryFeeAmount = feeCalculated ? deliveryFee : 50;
    const taxableAmount = subtotal + damageWaiverAmount + deliveryFeeAmount;
    return taxableAmount * 0.07; // 7% tax on all taxable components
  };

  const getPaymentAmount = () => {
    // If test coupon is applied, only charge $1 for testing purposes
    if (appliedCoupon && appliedCoupon.isTesting) {
      return 1.00;
    }
    
    const fullTotal = calculateFullTotal();
    return paymentOption === 'deposit' ? 50 : fullTotal;
  };

  const getRemainingAmount = () => {
    // If test coupon is applied, no remaining amount since we only charge $1 total
    if (appliedCoupon && appliedCoupon.isTesting) {
      return 0;
    }
    
    const fullTotal = calculateFullTotal();
    return paymentOption === 'deposit' ? Math.max(0, fullTotal - 50) : 0;
  };
  
  // Coupon handlers
  const handleCouponCodeChange = (e) => {
    setCouponCode(e.target.value.toUpperCase());
    setCouponError('');
  };
  
  const applyCouponCode = async () => {
    if (!couponCode.trim()) {
      setCouponError('Please enter a coupon code');
      return;
    }
    
    setCouponLoading(true);
    setCouponError('');
    
    try {
      const baseSubtotal = cart.total;
      const damageWaiverAmount = liabilityWaiver.purchaseDamageWaiver ? cart.total * 0.1 : 0;
      const deliveryFeeAmount = feeCalculated ? deliveryFee : 0;
      const overnightUpcharge = liabilityWaiver.overnightUpcharge ? 100 : 0;
      const taxableAmount = baseSubtotal + damageWaiverAmount + deliveryFeeAmount + overnightUpcharge;
      const taxAmount = taxableAmount * 0.07;
      const subtotal = baseSubtotal + deliveryFeeAmount + taxAmount + damageWaiverAmount + overnightUpcharge + tipAmount;
      const result = await validateCoupon(couponCode, subtotal);
      
      if (result.isValid) {
        const discount = calculateDiscount(result.coupon, subtotal);
        setAppliedCoupon(result.coupon);
        setCouponDiscount(discount);
        setCouponError('');
        
        // If it's a test coupon, automatically switch to full payment option
        if (result.coupon.isTesting) {
          setPaymentOption('full');
        }
      } else {
        setCouponError(result.error);
      }
    } catch (error) {
      console.error('Error applying coupon:', error);
      setCouponError('Failed to apply coupon. Please try again.');
    } finally {
      setCouponLoading(false);
    }
  };
  
  const removeCoupon = () => {
    const wasTestCoupon = appliedCoupon && appliedCoupon.isTesting;
    setAppliedCoupon(null);
    setCouponDiscount(0);
    setCouponCode('');
    setCouponError('');
    
    // If removing a test coupon, reset to deposit option
    if (wasTestCoupon) {
      setPaymentOption('deposit');
    }
  };
  
  // Outlet question and generator upsell handlers
  const handleOutletQuestion = (hasOutlet) => {
    setOutletWithin100Feet(hasOutlet);
    if (!hasOutlet) {
      setShowGeneratorUpsell(true);
    }
  };
  
  const handleGeneratorUpsell = (addGenerator) => {
    if (addGenerator) {
      // Add generator to cart
      const generator = {
        id: 'generator-addon',
        name: 'Generator Rental',
        price: '$125/day',
        priceNumeric: 125,
        mainImage: '/images/generator.png',
        isAddon: true
      };
      
      // Create booking info for the generator (same date as other items if available)
      const bookingInfo = {
        date: cart.items.length > 0 ? cart.items[0].date : new Date(),
        time: cart.items.length > 0 ? cart.items[0].time : '9:00 AM'
      };
      
      // Add generator to the cart using the cart context
      addToCart(generator, bookingInfo);
      setGeneratorAdded(true);
    }
    setShowGeneratorUpsell(false);
  };
  
  useEffect(() => {
    // Check if user is coming as a guest
    const params = new URLSearchParams(location.search);
    const mode = params.get('mode');
    if (mode === 'guest') {
      setIsGuest(true);
    }
    
    // Check if user is logged in
    const isLoggedIn = localStorage.getItem('isLoggedIn') === 'true';
    
    if (isLoggedIn && !isGuest) {
      // Get user info from localStorage
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      if (user) {
        setCustomerInfo({
          firstName: user.firstName || user.name?.split(' ')[0] || '',
          lastName: user.lastName || user.name?.split(' ')[1] || '',
          email: user.email || '',
          phone: user.phone || ''
        });
      }
    }
    
    // Check if cart is empty
    if (!cart || cart.items.length === 0) {
      navigate('/rentals');
    } else {
      // Pre-fill delivery date and time from cart items if available
      const firstItem = cart.items[0];
      if (firstItem) {
        // Extract start date from the first cart item (prioritize new format)
        let bookingStartDate = '';
        let bookingEndDate = '';
        
        if (firstItem.startDate) {
          const date = new Date(firstItem.startDate);
          bookingStartDate = date.toISOString().split('T')[0];
        } else if (firstItem.date) {
          const date = new Date(firstItem.date);
          bookingStartDate = date.toISOString().split('T')[0];
        }
        
        if (firstItem.endDate) {
          const date = new Date(firstItem.endDate);
          bookingEndDate = date.toISOString().split('T')[0];
        } else if (firstItem.date) {
          // Fallback to same day if no end date
          const date = new Date(firstItem.date);
          bookingEndDate = date.toISOString().split('T')[0];
        }
        
        // Extract start and end times (prioritize new format)
        const bookingStartTime = firstItem.startTime || firstItem.time || '';
        const bookingEndTime = firstItem.endTime || firstItem.time || '';
        
        setDeliveryInfo(prev => ({
          ...prev,
          deliveryDate: bookingStartDate, // Use start date for delivery
          deliveryTime: bookingStartTime, // Use start time for delivery
          // Store additional rental period info
          rentalStartDate: bookingStartDate,
          rentalEndDate: bookingEndDate,
          rentalStartTime: bookingStartTime,
          rentalEndTime: bookingEndTime
        }));
      }
    }
  }, [location, navigate, cart, isGuest]);

  const handleCustomerInfoChange = (e) => {
    const { name, value } = e.target;
    setCustomerInfo({
      ...customerInfo,
      [name]: value
    });
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };
  
  const handleDeliveryInfoChange = async (e) => {
    const { name, value } = e.target;
    
    // Create updated delivery info
    const updatedDeliveryInfo = {
      ...deliveryInfo,
      [name]: value
    };
    
    // If city is changed, automatically set the state based on the selected city
    if (name === 'city' && value) {
      const selectedCity = serviceAreas.find(area => area.city === value);
      if (selectedCity) {
        updatedDeliveryInfo.state = selectedCity.state;
      }
    }
    
    // If zipCode is changed, make sure city and state are consistent
    if (name === 'zipCode' && value) {
      const selectedArea = serviceAreas.find(area => 
        area.zipCodes.includes(value)
      );
      if (selectedArea) {
        updatedDeliveryInfo.city = selectedArea.city;
        updatedDeliveryInfo.state = selectedArea.state;
      }
    }
    
    setDeliveryInfo(updatedDeliveryInfo);
    
    // Calculate delivery fee when both city and zip code are selected
    if (updatedDeliveryInfo.city && updatedDeliveryInfo.zipCode) {
      try {
        const fee = await calculateDeliveryFee(updatedDeliveryInfo.city, updatedDeliveryInfo.zipCode);
        setDeliveryFee(fee);
      } catch (error) {
        console.error('Error calculating delivery fee:', error);
        setFeeCalculated(false);
        setDeliveryFee(50); // Fallback fee
      }
    } else {
      // Reset fee calculated flag if either city or zip is not set
      setFeeCalculated(false);
      setDeliveryFee(0);
    }
    
    // Clear error for this field if it exists
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };
  
  const handlePaymentInfoChange = (e) => {
    const { name, value, type, checked } = e.target;
    setPaymentInfo({
      ...paymentInfo,
      [name]: type === 'checkbox' ? checked : value
    });
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };
  
  const validateStep = (step) => {
    // Create a new errors object instead of modifying the existing one
    const newErrors = {};
    
    try {
      if (step === 1) {
        if (!customerInfo.firstName) newErrors.firstName = 'First name is required';
        if (!customerInfo.lastName) newErrors.lastName = 'Last name is required';
        if (!customerInfo.email) newErrors.email = 'Email is required';
        else if (!/\S+@\S+\.\S+/.test(customerInfo.email)) newErrors.email = 'Email is invalid';
        if (!customerInfo.phone) newErrors.phone = 'Phone number is required';
      }
      
      // Ordering paused for this cart's warehouse — stop at every step rather
      // than letting the customer fill out four screens for nothing.
      if (orderingPause) {
        newErrors.orderingPaused = orderingPause.message;
      }

      if (step === 2) {
        if (!deliveryInfo.address) newErrors.address = 'Address is required';
        if (!deliveryInfo.city) newErrors.city = 'City is required';
        // Don't validate state since it's auto-selected based on city
        if (!deliveryInfo.zipCode) newErrors.zipCode = 'ZIP code is required';
        if (!deliveryInfo.deliveryDate) newErrors.deliveryDate = 'Delivery date is required';
        if (!deliveryInfo.deliveryTime) newErrors.deliveryTime = 'Delivery time is required';

        // Strictly enforce that the delivery address is within range of the
        // office that stocks the cart's units (see calculateDeliveryFee).
        const { mixed } = getCartFulfillmentOffice();
        if (mixed) {
          newErrors.deliveryRange =
            'Your cart contains units from both our Angola and Elkhart locations. ' +
            'Please place a separate order for each location.';
        } else if (deliveryRangeError) {
          newErrors.deliveryRange = deliveryRangeError;
        }
      }

      // Add validation for liability waiver step
      if (step === 3) {
        if (!liabilityWaiver.termsAgreed) newErrors.termsAgreed = 'You must agree to the terms and conditions';
        if (!liabilityWaiver.surfaceType) newErrors.surfaceType = 'Please specify the surface type';
        if (outletWithin100Feet === null) newErrors.outletQuestion = 'Please answer the outlet question to continue';
      }
      
      if (step === 4) {
        if (!paymentInfo.cardHolder) newErrors.cardHolder = 'Card holder name is required';
        // Only check paymentMethod on the final submission
        // if (!paymentMethod) newErrors.cardElement = 'Please enter valid card details';
      }
      
      // Set the errors state with the new errors
      setErrors(newErrors);
      
      // Log validation results for debugging
      console.log('Validation errors for step', step, ':', newErrors);
      
      // Return true if there are no errors
      return Object.keys(newErrors).length === 0;
    } catch (error) {
      console.error('Error in validateStep:', error);
      // If there's an error, return true to allow proceeding to the next step
      // This prevents users from getting stuck if there's a validation bug
      return true;
    }
  };
  
  const goToNextStep = () => {
    // Clear any previous errors
    setErrors({});
    
    try {
      // Add console logging to help debug
      console.log('Current step:', activeStep);
      console.log('Validating step...');
      
      const isValid = validateStep(activeStep);
      console.log('Validation result:', isValid);
      console.log('Validation errors:', errors);
      
      if (isValid) {
        console.log('Moving to next step:', activeStep + 1);
        setActiveStep(prevStep => prevStep + 1);
      } else {
        console.log('Validation failed, staying on step:', activeStep);
      }
    } catch (error) {
      console.error('Error in goToNextStep:', error);
      // Show a user-friendly error message
      alert('There was an error processing your request. Please try again.');
    }
  };
  
  const goToPrevStep = () => {
    setActiveStep(activeStep - 1);
  };
  
  const handlePaymentSubmit = (paymentMethodObj) => {
    console.log('[CheckoutPage] Payment method received in handlePaymentSubmit:', paymentMethodObj);
    setPaymentMethod(paymentMethodObj);
    // Store payment method in session storage to persist between steps
    try {
      sessionStorage.setItem('roweBros_paymentMethod', JSON.stringify(paymentMethodObj));
      console.log('[CheckoutPage] Payment method saved to sessionStorage:', paymentMethodObj);
    } catch (error) {
      console.error('[CheckoutPage] Error storing payment method:', error);
    }
    goToNextStep();
  };
  
  const buildDeliveryInfoForStripe = (deliveryInfo) => {
    // If address is already an object with line1, return as is
    if (deliveryInfo && typeof deliveryInfo.address === 'object' && deliveryInfo.address !== null && deliveryInfo.address.line1 !== undefined) {
      return deliveryInfo;
    }
    // If address is a string or missing, build the object for Stripe
    return {
      ...deliveryInfo,
      address: {
        line1: deliveryInfo.address || '',
        line2: '',
        city: deliveryInfo.city || '',
        state: deliveryInfo.state || '',
        postal_code: deliveryInfo.zipCode || '',
        country: 'US',
      },
    };
  };

  // Function to send customer purchase data to webhook
  const sendPurchaseWebhook = async (orderData, customerData, deliveryData, cartData, paymentData) => {
    try {
      const webhookUrl = 'https://services.leadconnectorhq.com/hooks/FynKHfyY9oPrJ1ojOFxi/webhook-trigger/8ac544e3-f241-4e81-9115-f617719a20d1';
      
      // Calculate totals
      const subtotal = cart.total;
      const damageWaiverAmount = liabilityWaiver.purchaseDamageWaiver ? cart.total * 0.1 : 0;
      const deliveryFeeAmount = feeCalculated ? deliveryFee : 50;
      const taxAmount = calculateTax();
      const totalAmount = subtotal + damageWaiverAmount + deliveryFeeAmount + taxAmount + tipAmount;
      
      // Prepare the webhook payload with comprehensive order information
      const webhookPayload = {
        // Order Information
        orderId: orderData.orderId,
        orderDate: new Date().toISOString(),
        paymentIntentId: paymentData.paymentIntentId,
        
        // Customer Information
        customer: {
          firstName: customerData.firstName,
          lastName: customerData.lastName,
          fullName: `${customerData.firstName} ${customerData.lastName}`,
          email: customerData.email,
          phone: customerData.phone
        },
        
        // Delivery Information
        delivery: {
          address: deliveryData.address,
          city: deliveryData.city,
          state: deliveryData.state,
          zipCode: deliveryData.zipCode,
          fullAddress: `${deliveryData.address}, ${deliveryData.city}, ${deliveryData.state} ${deliveryData.zipCode}`,
          deliveryDate: deliveryData.deliveryDate,
          deliveryTime: deliveryData.deliveryTime,
          specialInstructions: deliveryData.deliveryInstructions || '',
          deliveryFee: deliveryFee
        },
        
        // Product Information
        items: cartData.items.map(item => ({
          id: item.id,
          name: item.name,
          price: item.priceNumeric,
          quantity: item.quantity,
          rentalDate: item.date,
          rentalTime: item.time,
          image: item.mainImage,
          isAddon: item.isAddon || false
        })),
        
        // Financial Information
        pricing: {
          subtotal: subtotal,
          deliveryFee: deliveryFeeAmount,
          taxAmount: taxAmount,
          damageWaiver: damageWaiverAmount,
          tipAmount: tipAmount,
          tipPercentage: tipPercentage,
          totalAmount: totalAmount,
          currency: 'USD'
        },
        
        // Additional Services & Waivers
        services: {
          damageWaiverPurchased: liabilityWaiver.purchaseDamageWaiver,
          surfaceType: liabilityWaiver.surfaceType,
          outletWithin100Feet: outletWithin100Feet,
          generatorAdded: generatorAdded,
          termsAgreed: liabilityWaiver.termsAgreed
        },
        
        // Business Information
        business: {
          companyName: 'Rowe Bros Entertainment',
          orderSource: 'Website',
          timestamp: new Date().toISOString()
        }
      };
      
      console.log('Sending purchase webhook:', webhookPayload);
      
      const webhookResponse = await fetch(webhookUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(webhookPayload)
      });
      
      if (webhookResponse.ok) {
        console.log('Purchase webhook sent successfully');
      } else {
        console.error('Failed to send purchase webhook:', webhookResponse.status, webhookResponse.statusText);
      }
      
    } catch (error) {
      console.error('Error sending purchase webhook:', error);
      // Don't throw error to prevent interrupting the order flow
    }
  };

  const handlePlaceOrder = async () => {
    // Final gate: never charge a card for a location that stopped taking
    // orders — including a pause flipped while this page was open.
    if (orderingPause) {
      setPaymentError(orderingPause.message);
      return;
    }

    try {
      console.log('Placing order...');
      setIsProcessing(true);
      setPaymentError('');
      
      // Try to load payment method from session storage if not in state
      let paymentMethodToUse = paymentMethod;
      console.log('[CheckoutPage] Payment method at start of handlePlaceOrder:', paymentMethodToUse);
      if (!paymentMethodToUse) {
        try {
          const savedPaymentMethod = sessionStorage.getItem('roweBros_paymentMethod');
          if (savedPaymentMethod) {
            paymentMethodToUse = JSON.parse(savedPaymentMethod);
            setPaymentMethod(paymentMethodToUse);
            console.log('[CheckoutPage] Loaded payment method from session storage for order placement:', paymentMethodToUse);
          }
        } catch (error) {
          console.error('[CheckoutPage] Error loading payment method from session storage:', error);
        }
      }
      
      if (!paymentMethodToUse) {
        console.log('[CheckoutPage] Payment method missing at order placement');
        setPaymentError('Payment method is required. Please go back to the payment step and enter your card details.');
        setIsProcessing(false);
        return;
      }
      
      // STEP 1: Create a payment intent
      console.log('Creating payment intent...');
      const deliveryForStripe = buildDeliveryInfoForStripe(deliveryInfo);
      console.log('[CheckoutPage] Delivery info being sent to Stripe:', deliveryForStripe);
      
      // Calculate payment amount based on selected option (full or deposit)
      const paymentAmount = getPaymentAmount();
      const fullTotal = calculateFullTotal();
      const remainingAmount = getRemainingAmount();
      
      // Handle free orders or orders below Stripe's minimum ($0.50)
      const STRIPE_MINIMUM_CHARGE = 0.50;
      const isFreeOrder = paymentAmount === 0;
      const chargeAmount = Math.max(paymentAmount, isFreeOrder ? 0 : STRIPE_MINIMUM_CHARGE);
      const amount = Math.round(chargeAmount * 100);
      
      console.log('Order totals:', { 
        fullTotal,
        paymentAmount, 
        remainingAmount,
        paymentOption,
        isFreeOrder, 
        chargeAmount,
        amount 
      });

      if (isFreeOrder) {
        // Handle completely free orders - skip payment processing
        console.log('Processing free order - skipping payment');
        
        // STEP 3: Save the order to Firebase directly (no payment needed)
        try {
          const response = await fetch('/.netlify/functions/save-order', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              paymentIntentId: 'free-order-' + Date.now(), // Generate a unique ID for free orders
              customerInfo,
              deliveryInfo,
              cartItems: cart.items,
              liabilityWaiver,
              damageWaiverCost: liabilityWaiver.purchaseDamageWaiver ? cart.total * 0.1 : 0,
              overnightUpchargeCost: liabilityWaiver.overnightUpcharge ? 100 : 0,
              tipAmount: tipAmount,
              tipPercentage: tipPercentage,
              appliedCoupon: appliedCoupon,
              couponDiscount: couponDiscount,
              isFreeOrder: true,
              isTestOrder: appliedCoupon && appliedCoupon.isTesting,
              paymentOption: paymentOption,
              fullTotal: fullTotal,
              paidAmount: 0,
              remainingAmount: fullTotal,
            }),
          });

          if (!response.ok) {
            // Handle double booking conflicts for free orders too
            if (response.status === 409) {
              const errorData = await response.json();
              setPaymentError(`⚠️ Booking Conflict: ${errorData.message}`);
              setPaymentStatus('Double booking detected');
              // Clear the cart since we need to start over
              clearCart();
              return false;
            }
            
            const errorData = await response.text();
            console.error('Server error:', errorData);
            throw new Error(`Server error: ${response.status} ${errorData}`);
          }

          const orderData = await response.json();
          console.log('Free order saved successfully:', orderData);
          
          // Send purchase webhook
          await sendPurchaseWebhook(orderData, customerInfo, deliveryInfo, cart, { 
            paymentIntentId: orderData.paymentIntentId || 'free-order',
            isFreeOrder: true 
          });
          
          // Apply coupon usage if one was used
          if (appliedCoupon) {
            try {
              await applyCoupon(appliedCoupon.id);
              console.log('Coupon usage applied for free order');
            } catch (couponError) {
              console.error('Error applying coupon usage:', couponError);
            }
          }
          
          // Clear the cart
          clearCart();
          
          // Show success message and redirect to confirmation page
          setPaymentStatus('success');
          setOrderId(orderData.orderId);
          
          // Redirect to confirmation page
          navigate('/thank-you', { 
            state: { 
              orderId: orderData.orderId,
              orderDetails: orderData.orderDetails,
              isFreeOrder: true
            } 
          });
          
          return true;
        } catch (error) {
          console.error('Error saving free order to database:', error);
          setPaymentStatus('error');
          setPaymentError(`Error saving free order: ${error.message}`);
          return false;
        }
      } else {
        // Handle paid orders through normal Stripe flow
        const { clientSecret, paymentIntentId } = await createPaymentIntent(
          amount,
          customerInfo,
          deliveryForStripe,
          cart
        );
        
        console.log('Payment intent created:', { paymentIntentId, clientSecret });
        
        // STEP 2: Process the payment with the payment method
        console.log('Processing payment...');
        const result = await processPayment(clientSecret, paymentMethodToUse);
        
        console.log('Payment processed:', result);
        
        if (result.success) {
          // Handle payment processing
          try {
            console.log('About to save order to database...');
            
            let saveOrderResult;
            
            try {
              // Try Netlify function first
              const saveOrderResponse = await fetch('/.netlify/functions/save-order', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  paymentIntentId: result.paymentIntentId,
                  customerInfo,
                  deliveryInfo,
                  cartItems: cart.items,
                  liabilityWaiver,
                  damageWaiverCost: liabilityWaiver.purchaseDamageWaiver ? cart.total * 0.1 : 0,
                  overnightUpchargeCost: liabilityWaiver.overnightUpcharge ? 100 : 0,
                  tipAmount: tipAmount,
                  tipPercentage: tipPercentage,
                  appliedCoupon: appliedCoupon,
                  couponDiscount: couponDiscount,
                  paymentOption: paymentOption,
                  fullTotal: fullTotal,
                  paidAmount: paymentAmount,
                  remainingAmount: remainingAmount,
                  isTestOrder: appliedCoupon && appliedCoupon.isTesting,
                }),
              });
              
              saveOrderResult = await saveOrderResponse.json();
              console.log('Save order result:', saveOrderResult);
              
              if (!saveOrderResponse.ok) {
                // Handle double booking conflicts
                if (saveOrderResponse.status === 409) {
                  setPaymentError(`⚠️ Booking Conflict: ${saveOrderResult.message}`);
                  setPaymentStatus('Double booking detected');
                  // Clear the cart since we need to start over
                  clearCart();
                  return;
                }
                
                throw new Error(saveOrderResult.message || 'Netlify function failed');
              }
              
            } catch (netlifyError) {
              console.log('Netlify function failed, using direct Firebase save fallback...', netlifyError.message);
              
              // FALLBACK: Direct Firebase save when Netlify functions aren't available
              const { addDoc, collection } = await import('firebase/firestore');
              const { db } = await import('../../firebase');
              
              const orderData = {
                paymentIntentId: result.paymentIntentId,
                status: 'Pending',
                customer: {
                  name: `${customerInfo.firstName} ${customerInfo.lastName}`,
                  email: customerInfo.email,
                  phone: customerInfo.phone,
                  customerId: customerInfo.uid || null
                },
                delivery: {
                  address: deliveryInfo.address,
                  city: deliveryInfo.city,
                  state: deliveryInfo.state,
                  zipCode: deliveryInfo.zipCode,
                  date: deliveryInfo.deliveryDate,
                  time: deliveryInfo.deliveryTime,
                  instructions: deliveryInfo.deliveryInstructions
                },
                rentalPeriod: {
                  startDate: deliveryInfo.rentalStartDate || cart.items[0]?.startDate || cart.items[0]?.date,
                  endDate: deliveryInfo.rentalEndDate || cart.items[0]?.endDate || cart.items[0]?.date,
                  startTime: deliveryInfo.rentalStartTime || cart.items[0]?.startTime || cart.items[0]?.time,
                  endTime: deliveryInfo.rentalEndTime || cart.items[0]?.endTime || cart.items[0]?.time
                },
                items: cart.items.map(item => ({
                  id: item.id,
                  name: item.name,
                  price: typeof item.price === 'number' ? item.price : parseFloat(item.price?.toString().replace(/[^0-9.]/g, '')) || 0,
                  quantity: item.quantity,
                  image: item.image || item.mainImage,
                  startDate: item.startDate,
                  endDate: item.endDate,
                  startTime: item.startTime,
                  endTime: item.endTime,
                  date: item.date || item.startDate,
                  time: item.time || item.startTime,
                  bookingId: item.bookingId
                })),
                payment: {
                  id: result.paymentIntentId,
                  amount: fullTotal || cart.total,
                  status: 'succeeded',
                  method: 'card'
                },
                pricing: {
                  subtotal: cart.items.reduce((sum, item) => sum + (item.price * item.quantity), 0),
                  deliveryFee: deliveryFee,
                  tax: calculateTax(),
                  total: fullTotal || cart.total,
                  damageWaiverCost: liabilityWaiver.purchaseDamageWaiver ? cart.total * 0.1 : 0,
                  tipAmount: tipAmount || 0,
                  tipPercentage: tipPercentage || 0,
                  couponDiscount: couponDiscount || 0
                },
                appliedCoupon: appliedCoupon || null,
                liabilityWaiver: liabilityWaiver || {},
                paymentOption: paymentOption || 'full',
                paymentInfo: {
                  fullTotal: fullTotal || cart.total,
                  paidAmount: paymentAmount,
                  remainingAmount: remainingAmount || 0,
                  isTestOrder: appliedCoupon && appliedCoupon.isTesting || false
                },
                customerId: customerInfo.uid || null,
                total: fullTotal || cart.total,
                subtotal: cart.items.reduce((sum, item) => sum + (item.price * item.quantity), 0),
                createdAt: new Date(),
                updatedAt: new Date(),
                source: 'website'
              };
              
              console.log('Saving order directly to Firebase...');
              const docRef = await addDoc(collection(db, 'orders'), orderData);
              console.log('Order saved successfully via Firebase fallback with ID:', docRef.id);
              
              // Create mock response matching Netlify function format
              saveOrderResult = {
                success: true,
                orderId: docRef.id,
                orderDetails: { ...orderData, id: docRef.id }
              };
            }
            
            // Extract order ID from response  
            const orderId = saveOrderResult.orderId || saveOrderResult.orderDetails?.id;
            if (!orderId) {
              throw new Error('Order was saved but no order ID was returned');
            }
            
            console.log('Order saved successfully with ID:', orderId);
            setOrderId(orderId);
            setOrderComplete(true);
            setPaymentStatus('Order completed successfully!');
            
            // Send purchase webhook
            await sendPurchaseWebhook(saveOrderResult, customerInfo, deliveryInfo, cart, { paymentIntentId: result.paymentIntentId });
            
            // Apply coupon usage if one was used
            if (appliedCoupon) {
              try {
                await applyCoupon(appliedCoupon.id);
                console.log('Coupon usage applied');
              } catch (couponError) {
                console.error('Error applying coupon usage:', couponError);
              }
            }
            
            // Clear the cart
            clearCart();
            
            // Show success message and redirect to confirmation page
            setPaymentStatus('success');
            setOrderId(orderId);
            
            // Redirect to confirmation page
            navigate('/thank-you', { 
              state: { 
                orderId: orderId,
                orderDetails: saveOrderResult.orderDetails,
                isFreeOrder: false
              } 
            });
          } catch (error) {
            console.error('Error in order processing:', error);
            setPaymentError(`Error saving order: ${error.message}`);
            return false;
          }
        } else {
          setPaymentError('Payment processing failed. Please try again.');
        }
      }
      
      setIsProcessing(false);
    } catch (error) {
      console.error('Error placing order:', error);
      setPaymentError(`An error occurred while processing your payment: ${error.message}`);
      setIsProcessing(false);
    }
  };
  
  const renderStepContent = () => {
    switch (activeStep) {
      case 1:
        return renderCustomerInfoForm();
      case 2:
        return renderDeliveryInfoForm();
      case 3:
        return renderLiabilityWaiverForm();
      case 4:
        return renderPaymentForm();
      case 5:
        return renderOrderReview();
      default:
        return null;
    }
  };

  const renderCustomerInfoForm = () => {
    return (
      <div className="form-section">
        <h2>Customer Information</h2>
        <div className="form-grid">
          <div className="form-group">
            <label htmlFor="firstName">
              <FontAwesomeIcon icon={faUser} /> First Name
            </label>
            <input
              type="text"
              id="firstName"
              name="firstName"
              value={customerInfo.firstName}
              onChange={handleCustomerInfoChange}
              placeholder="Enter your first name"
            />
            {errors.firstName && <div className="input-error">{errors.firstName}</div>}
          </div>
          
          <div className="form-group">
            <label htmlFor="lastName">
              <FontAwesomeIcon icon={faUser} /> Last Name
            </label>
            <input
              type="text"
              id="lastName"
              name="lastName"
              value={customerInfo.lastName}
              onChange={handleCustomerInfoChange}
              placeholder="Enter your last name"
            />
            {errors.lastName && <div className="input-error">{errors.lastName}</div>}
          </div>
          
          <div className="form-group">
            <label htmlFor="email">
              <FontAwesomeIcon icon={faEnvelope} /> Email Address
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={customerInfo.email}
              onChange={handleCustomerInfoChange}
              placeholder="Enter your email"
            />
            {errors.email && <div className="input-error">{errors.email}</div>}
          </div>
          
          <div className="form-group">
            <label htmlFor="phone">
              <FontAwesomeIcon icon={faPhone} /> Phone Number
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={customerInfo.phone}
              onChange={handleCustomerInfoChange}
              placeholder="Enter your phone number"
            />
            {errors.phone && <div className="input-error">{errors.phone}</div>}
          </div>
        </div>
      </div>
    );
  };
  
  const renderDeliveryInfoForm = () => {
    // Determine the current state based on selected city
    const currentState = deliveryInfo.state || (deliveryInfo.city ? 
      serviceAreas.find(area => area.city === deliveryInfo.city)?.state || 'IN' : 'IN');

    // Get unique cities from service areas
    const availableCities = [...new Set(serviceAreas.map(area => area.city))].sort();
    
    // Get available ZIP codes for selected city
    const availableZipCodes = deliveryInfo.city ? 
      serviceAreas
        .filter(area => area.city === deliveryInfo.city)
        .flatMap(area => area.zipCodes)
        .filter(zip => zip) // Remove empty zip codes
        .sort()
      : [];

    return (
      <div className="form-section">
        <h2>Delivery Information</h2>
        <div className="service-area-notice">
          <FontAwesomeIcon icon={faTruck} />
          {locationsLoading ? (
            <p>Loading service areas...</p>
          ) : locationsError ? (
            <p style={{ color: '#e74c3c' }}>⚠️ {locationsError}</p>
          ) : (
            <p>We currently serve {serviceAreas.length} location{serviceAreas.length !== 1 ? 's' : ''} across Indiana and Michigan. Delivery fees vary by location.</p>
          )}
        </div>
        {(deliveryRangeError || errors.deliveryRange) && (
          <div
            className="delivery-range-error"
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '0.6rem',
              background: '#fdecea',
              border: '1px solid #f5c6cb',
              color: '#a12a21',
              borderRadius: '6px',
              padding: '0.85rem 1rem',
              marginBottom: '1rem',
              fontSize: '0.95rem',
              lineHeight: 1.4
            }}
          >
            <FontAwesomeIcon icon={faExclamationTriangle} style={{ marginTop: '0.15rem' }} />
            <span>{deliveryRangeError || errors.deliveryRange}</span>
          </div>
        )}
        <div className="form-grid">
          <div className="form-group full-width">
            <label htmlFor="address">
              <FontAwesomeIcon icon={faMapMarkerAlt} /> Address
            </label>
            <input
              type="text"
              id="address"
              name="address"
              value={deliveryInfo.address}
              onChange={handleDeliveryInfoChange}
              placeholder="Enter your street address"
            />
            {errors.address && <div className="input-error">{errors.address}</div>}
          </div>
          
          <div className="form-group">
            <label htmlFor="city">City</label>
            <select
              id="city"
              name="city"
              value={deliveryInfo.city}
              onChange={handleDeliveryInfoChange}
              disabled={locationsLoading}
            >
              <option value="">
                {locationsLoading ? 'Loading cities...' : 'Select a city'}
              </option>
              {availableCities.map((city, index) => (
                <option key={index} value={city}>{city}</option>
              ))}
            </select>
            {errors.city && <div className="input-error">{errors.city}</div>}
          </div>
          
          <div className="form-group">
            <label htmlFor="state">State</label>
            <select
              id="state"
              name="state"
              value={currentState}
              onChange={handleDeliveryInfoChange}
              disabled
            >
              <option value="IN">Indiana</option>
              <option value="MI">Michigan</option>
            </select>
            <div className="field-note">We only serve IN and MI</div>
            {errors.state && <div className="input-error">{errors.state}</div>}
          </div>
          
          <div className="form-group">
            <label htmlFor="zipCode">ZIP Code</label>
            <select
              id="zipCode"
              name="zipCode"
              value={deliveryInfo.zipCode}
              onChange={handleDeliveryInfoChange}
              disabled={locationsLoading || !deliveryInfo.city}
            >
              <option value="">
                {locationsLoading ? 'Loading...' : 
                 !deliveryInfo.city ? 'Select a city first' : 
                 'Select a ZIP code'}
              </option>
              {availableZipCodes.map((zip, index) => (
                <option key={index} value={zip}>{zip}</option>
              ))}
            </select>
            {errors.zipCode && <div className="input-error">{errors.zipCode}</div>}
          </div>
          
          <div className="form-group">
            <label htmlFor="deliveryDate">
              <FontAwesomeIcon icon={faCalendarAlt} /> Delivery Date
            </label>
            <div className="pre-selected-field">
              <input
                type="date"
                id="deliveryDate"
                name="deliveryDate"
                value={deliveryInfo.deliveryDate}
                onChange={handleDeliveryInfoChange}
                min={new Date().toISOString().split('T')[0]}
                className="pre-filled-input"
                readOnly={!!deliveryInfo.deliveryDate}
              />
              {deliveryInfo.deliveryDate && (
                <div className="pre-selected-badge">
                  <FontAwesomeIcon icon={faCheck} /> Pre-selected
                </div>
              )}
            </div>
            {errors.deliveryDate && <div className="input-error">{errors.deliveryDate}</div>}
          </div>
          
          <div className="form-group">
            <label htmlFor="deliveryTime">
              <FontAwesomeIcon icon={faClock} /> Delivery Time
            </label>
            <div className="pre-selected-field">
              <select
                id="deliveryTime"
                name="deliveryTime"
                value={deliveryInfo.deliveryTime}
                onChange={handleDeliveryInfoChange}
                className="pre-filled-input"
                disabled={!!deliveryInfo.deliveryTime}
              >
                <option value="">Select a time</option>
                <option value="9:00 AM">9:00 AM</option>
                <option value="10:00 AM">10:00 AM</option>
                <option value="11:00 AM">11:00 AM</option>
                <option value="12:00 PM">12:00 PM</option>
                <option value="1:00 PM">1:00 PM</option>
                <option value="2:00 PM">2:00 PM</option>
                <option value="3:00 PM">3:00 PM</option>
                <option value="4:00 PM">4:00 PM</option>
                <option value="5:00 PM">5:00 PM</option>
                <option value="6:00 PM">6:00 PM</option>
              </select>
              {deliveryInfo.deliveryTime && (
                <div className="pre-selected-badge">
                  <FontAwesomeIcon icon={faCheck} /> Pre-selected
                </div>
              )}
            </div>
            {errors.deliveryTime && <div className="input-error">{errors.deliveryTime}</div>}
          </div>
          
          <div className="form-group full-width">
            <label htmlFor="deliveryInstructions">
              <FontAwesomeIcon icon={faTruck} /> Delivery Instructions (Optional)
            </label>
            <textarea
              id="deliveryInstructions"
              name="deliveryInstructions"
              value={deliveryInfo.deliveryInstructions}
              onChange={handleDeliveryInfoChange}
              placeholder="Any special instructions for delivery?"
              rows="3"
            />
          </div>
        </div>
      </div>
    );
  };
  
  const renderLiabilityWaiverForm = () => {
    const damageWaiverCost = (cart.total * 0.1).toFixed(2);
    
    return (
      <div className="form-section">
        <h2>Waiver and Setup</h2>
        
        <div className="waiver-container">
          <div className="waiver-text">
            <p>By checking this box, I acknowledge that I understand and agree to the following:</p>
            <p>If anything happens to the products provided, I am responsible for the products. I agree to Rowe Bros' <span className="terms-link" onClick={() => setShowTermsModal(true)}>terms and conditions</span>.</p>
          </div>
          
          <div className="waiver-acceptance">
            <div className="form-group checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="termsAgreed"
                  checked={liabilityWaiver.termsAgreed}
                  onChange={handleLiabilityWaiverChange}
                />
                <span>I agree to the <span className="terms-link" onClick={() => setShowTermsModal(true)}>terms and conditions</span></span>
              </label>
              {errors.termsAgreed && <div className="input-error">{errors.termsAgreed}</div>}
            </div>
          </div>
          
          <div className="damage-waiver-container">
            <h3>Damage Waiver Option</h3>
            <p>Would you like to purchase our Damage Waiver? For 10% of your total rental costs (${damageWaiverCost}), we will waive any damage that happens to our equipment during your possession of said equipment, excluding intentional damage or theft.</p>
            
            <div className="form-group checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="purchaseDamageWaiver"
                  checked={liabilityWaiver.purchaseDamageWaiver}
                  onChange={handleLiabilityWaiverChange}
                />
                <span>Yes, I would like to purchase the Damage Waiver (10% - ${damageWaiverCost})</span>
              </label>
            </div>
          </div>

          <div className="overnight-upcharge-container">
            <h3>Overnight Rental Option</h3>
            <p>Would you like to keep the equipment until the next morning? This allows you to enjoy the rental longer and have flexible pickup timing.</p>
            
            <div className="form-group checkbox-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="overnightUpcharge"
                  checked={liabilityWaiver.overnightUpcharge}
                  onChange={handleLiabilityWaiverChange}
                />
                <span>Yes, I would like to keep it until the next morning (+$100)</span>
              </label>
            </div>
          </div>

          <div className="setup-container">
            <h3>Setup Information</h3>
            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="surfaceType">What type of surface?</label>
                <select
                  id="surfaceType"
                  name="surfaceType"
                  value={liabilityWaiver.surfaceType}
                  onChange={handleLiabilityWaiverChange}
                >
                  <option value="">Select surface type</option>
                  <option value="grass">Grass</option>
                  <option value="concrete">Concrete</option>
                  <option value="asphalt">Asphalt</option>
                  <option value="dirt">Dirt</option>
                  <option value="sand">Sand</option>
                  <option value="other">Other</option>
                </select>
                {errors.surfaceType && <div className="input-error">{errors.surfaceType}</div>}
              </div>
              
              <div className="form-group full-width">
                <label>Do you have an electrical outlet within 100 feet of where the bounce house will be set up?</label>
                <div className="outlet-question-buttons">
                  <button
                    type="button"
                    className={`outlet-btn ${outletWithin100Feet === true ? 'active yes' : ''}`}
                    onClick={() => handleOutletQuestion(true)}
                  >
                    <FontAwesomeIcon icon={faCheck} /> Yes, I have an outlet within 100 feet
                  </button>
                  <button
                    type="button"
                    className={`outlet-btn ${outletWithin100Feet === false ? 'active no' : ''}`}
                    onClick={() => handleOutletQuestion(false)}
                  >
                    <FontAwesomeIcon icon={faTimes} /> No, I don't have an outlet within 100 feet
                  </button>
                </div>
                {outletWithin100Feet === null && errors.outletQuestion && (
                  <div className="input-error">Please answer this question to continue</div>
                )}
                {generatorAdded && (
                  <div className="generator-added-notice">
                    <FontAwesomeIcon icon={faCheck} /> Generator rental added to your order!
                  </div>
                )}
              </div>
            </div>
          </div>
          
          <div className="additional-info">
            <div className="info-box">
              <h4>Important Information</h4>
              <ul>
                <li>Please note that we do require a Minimum Payment up front upon checkout that is not refundable, but will be credited toward the full balance.</li>
                <li>If you cancel within the 7 days prior to the event, we will retain that Minimum Payment in a Raincheck, good for one full year toward a future event.</li>
                <li>If you cancel before the 7 days leading up to the Event, your full balance can be refunded or placed in a Raincheck, whichever you prefer.</li>
                <li>Please note that we do require electricity per unit within 50' of the setup area.</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    );
  };
  
  const renderPaymentForm = () => {
    return (
      <div className="form-section">
        <h2>Payment Information</h2>
        <p className="secure-payment-note">
          <FontAwesomeIcon icon={faLock} /> All transactions are secure and encrypted
        </p>
        
        <Elements stripe={getStripe()}>
          <StripePaymentForm 
            paymentInfo={paymentInfo}
            handlePaymentInfoChange={handlePaymentInfoChange}
            errors={errors}
            onSubmit={handlePaymentSubmit}
          />
        </Elements>
        
        <div className="payment-methods">
          <div className="payment-method-title">Accepted Payment Methods</div>
          <div className="payment-icons">
            <img src="/images/visa.svg" alt="Visa" className="payment-icon" />
            <img src="/images/mastercard.svg" alt="Mastercard" className="payment-icon" />
            <img src="/images/amex.svg" alt="American Express" className="payment-icon" />
            <img src="/images/discover.svg" alt="Discover" className="payment-icon" />
          </div>
        </div>
      </div>
    );
  };
  
  const renderOrderReview = () => {
    return (
      <div className="form-section">
        <h2>Review Your Order</h2>
        
        <div className="review-sections">
          <div className="review-section">
            <h3>Customer Information</h3>
            <div className="review-details">
              <p><strong>Name:</strong> {customerInfo.firstName} {customerInfo.lastName}</p>
              <p><strong>Email:</strong> {customerInfo.email}</p>
              <p><strong>Phone:</strong> {customerInfo.phone}</p>
            </div>
          </div>
          
          <div className="review-section">
            <h3>Delivery Information</h3>
            <div className="review-details">
              <p><strong>Address:</strong> {deliveryInfo.address}</p>
              <p><strong>City:</strong> {deliveryInfo.city}, {deliveryInfo.state} {deliveryInfo.zipCode}</p>
              <p><strong>Delivery Date:</strong> {formatDateSafe(deliveryInfo.deliveryDate)}</p>
              <p><strong>Delivery Time:</strong> {deliveryInfo.deliveryTime}</p>
              {deliveryInfo.deliveryInstructions && (
                <p><strong>Instructions:</strong> {deliveryInfo.deliveryInstructions}</p>
              )}
              {feeCalculated && deliveryFee > 0 && (
                <p>
                  <strong>Delivery Fee:</strong> ${deliveryFee.toFixed(2)} for {deliveryInfo.city}
                  {/* Show distance info if available from the database location */}
                  {(() => {
                    const area = serviceAreas.find(area => 
                      area.city.toLowerCase() === deliveryInfo.city.toLowerCase()
                    );
                    if (area && area.calculation) {
                      return (
                        <span style={{ color: '#666', fontSize: '0.85rem', marginLeft: '0.5rem' }}>
                          ({area.calculation.distance} mi from {area.calculation.nearestOffice})
                        </span>
                      );
                    }
                    return null;
                  })()}
                </p>
              )}
            </div>
          </div>
          
          <div className="review-section">
            <h3>Waiver and Setup Information</h3>
            <div className="review-details">
              <p><strong>Terms & Conditions:</strong> Agreed</p>
              <p><strong>Damage Waiver:</strong> {liabilityWaiver.purchaseDamageWaiver ? 'Purchased' : 'Declined'}</p>
              {liabilityWaiver.purchaseDamageWaiver && (
                <p><strong>Damage Waiver Fee:</strong> ${(cart.total * 0.1).toFixed(2)} (10% of rental total)</p>
              )}
              <p><strong>Overnight Rental:</strong> {liabilityWaiver.overnightUpcharge ? 'Yes' : 'No'}</p>
              {liabilityWaiver.overnightUpcharge && (
                <p><strong>Overnight Upcharge:</strong> <span style={{color: '#FF6B35'}}>$100.00</span></p>
              )}
              <p><strong>Surface Type:</strong> {liabilityWaiver.surfaceType ? liabilityWaiver.surfaceType.charAt(0).toUpperCase() + liabilityWaiver.surfaceType.slice(1) : 'Not specified'}</p>
              <p><strong>Outlet within 100 feet:</strong> {
                outletWithin100Feet === null ? 'Not answered' : 
                outletWithin100Feet ? 'Yes' : 'No'
              }</p>
              {generatorAdded && (
                <p><strong>Generator Rental:</strong> <span style={{color: '#4CAF50'}}>Added to order ($125)</span></p>
              )}
            </div>
          </div>
          
          <div className="review-section">
            <h3>Payment Information</h3>
            <div className="review-details">
              <p><strong>Card Holder:</strong> {paymentInfo.cardHolder}</p>
              {paymentMethod && paymentMethod.card ? (
                <>
                  <p><strong>Card Number:</strong> **** **** **** {paymentMethod.card.last4}</p>
                  <p><strong>Expiration:</strong> {paymentMethod.card.exp_month}/{paymentMethod.card.exp_year}</p>
                </>
              ) : (
                <p>Card details will be collected at checkout</p>
              )}
            </div>
          </div>
        </div>
        
        <div className="review-section">
          <h3>Leave a Tip</h3>
          <div className="tip-container">
            <p>Show your appreciation for our delivery team!</p>
            
            <div className="tip-options">
              <div className="tip-percentages">
                <button 
                  type="button"
                  className={`tip-btn ${tipPercentage === 15 ? 'active' : ''}`}
                  onClick={() => handleTipPercentageChange(15)}
                >
                  15% (${(cart.total * 0.15).toFixed(2)})
                </button>
                <button 
                  type="button"
                  className={`tip-btn ${tipPercentage === 18 ? 'active' : ''}`}
                  onClick={() => handleTipPercentageChange(18)}
                >
                  18% (${(cart.total * 0.18).toFixed(2)})
                </button>
                <button 
                  type="button"
                  className={`tip-btn ${tipPercentage === 20 ? 'active' : ''}`}
                  onClick={() => handleTipPercentageChange(20)}
                >
                  20% (${(cart.total * 0.20).toFixed(2)})
                </button>
                <button 
                  type="button"
                  className={`tip-btn ${tipPercentage === 25 ? 'active' : ''}`}
                  onClick={() => handleTipPercentageChange(25)}
                >
                  25% (${(cart.total * 0.25).toFixed(2)})
                </button>
              </div>
              
              <div className="custom-tip">
                <label htmlFor="customTip">Custom Amount:</label>
                <div className="custom-tip-input">
                  <span className="dollar-sign">$</span>
                  <input
                    type="number"
                    id="customTip"
                    value={customTipAmount}
                    onChange={handleCustomTipChange}
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                  />
                </div>
              </div>
              
              <button 
                type="button"
                className={`tip-btn no-tip ${tipAmount === 0 ? 'active' : ''}`}
                onClick={() => {
                  setTipAmount(0);
                  setTipPercentage(0);
                  setCustomTipAmount('');
                }}
              >
                No Tip
              </button>
            </div>
            
            {tipAmount > 0 && (
              <div className="tip-selected">
                <p><strong>Tip Amount: ${tipAmount.toFixed(2)}</strong></p>
              </div>
            )}
          </div>
        </div>
        
        <div className="review-section">
          <h3>
            <FontAwesomeIcon icon={faTag} style={{ marginRight: '0.5rem' }} />
            Coupon Code
          </h3>
          <div className="coupon-container">
            {!appliedCoupon ? (
              <div className="coupon-input-section">
                <p>Have a coupon code? Enter it below to apply your discount:</p>
                <div className="coupon-input-group">
                  <input
                    type="text"
                    value={couponCode}
                    onChange={handleCouponCodeChange}
                    placeholder="Enter coupon code"
                    className="coupon-input"
                    maxLength={20}
                  />
                  <button
                    type="button"
                    onClick={applyCouponCode}
                    disabled={couponLoading || !couponCode.trim()}
                    className="apply-coupon-btn"
                  >
                    {couponLoading ? 'Applying...' : 'Apply'}
                  </button>
                </div>
                {couponError && (
                  <div className="coupon-error">
                    <FontAwesomeIcon icon={faTimes} style={{ marginRight: '0.5rem' }} />
                    {couponError}
                  </div>
                )}
              </div>
            ) : (
              <div className="coupon-applied">
                <div className="coupon-success">
                  <FontAwesomeIcon icon={faCheck} style={{ marginRight: '0.5rem', color: '#4CAF50' }} />
                  <span>Coupon "{appliedCoupon.code}" applied successfully!</span>
                </div>
                <div className="coupon-details">
                  <p><strong>Description:</strong> {appliedCoupon.description}</p>
                  <p><strong>Discount:</strong> {formatCouponValue(appliedCoupon)} (-${couponDiscount.toFixed(2)})</p>
                </div>
                <button
                  type="button"
                  onClick={removeCoupon}
                  className="remove-coupon-btn"
                >
                  <FontAwesomeIcon icon={faTimes} style={{ marginRight: '0.5rem' }} />
                  Remove Coupon
                </button>
              </div>
            )}
          </div>
        </div>
        
        <div className="review-section">
          <h3>
            <FontAwesomeIcon icon={faCreditCard} style={{ marginRight: '0.5rem' }} />
            Payment Options
          </h3>
          <div className="payment-options-container">
            {appliedCoupon && appliedCoupon.isTesting ? (
              <div className="test-coupon-notice">
                <p><strong>🧪 Test Coupon Applied</strong></p>
                <p>For testing purposes, you will only be charged $1.00 to process this order.</p>
              </div>
            ) : (
              <p>Choose how you would like to pay for your rental:</p>
            )}
            
            <div className="payment-option-group">
              <div className={`payment-option ${paymentOption === 'deposit' ? 'selected' : ''} ${appliedCoupon && appliedCoupon.isTesting ? 'disabled' : ''}`}>
                <label>
                  <input
                    type="radio"
                    name="paymentOption"
                    value="deposit"
                    checked={paymentOption === 'deposit'}
                    onChange={() => handlePaymentOptionChange('deposit')}
                    disabled={appliedCoupon && appliedCoupon.isTesting}
                  />
                  <div className="payment-option-content">
                    <div className="payment-option-header">
                      <h4>Pay $50 Deposit {appliedCoupon && appliedCoupon.isTesting ? '(Not available for test coupons)' : '(Recommended)'}</h4>
                      <span className="payment-amount">${appliedCoupon && appliedCoupon.isTesting ? '1.00' : '50.00'}</span>
                    </div>
                    <p className="payment-description">
                      {appliedCoupon && appliedCoupon.isTesting ? 
                        'Test coupons process the full $1.00 charge instead of using a deposit system.' :
                        'Pay a small deposit now and the remaining balance after your rental is delivered and set up. This is our most popular option as you can inspect everything before final payment.'
                      }
                    </p>
                    {paymentOption === 'deposit' && !appliedCoupon?.isTesting && (
                      <div className="payment-breakdown">
                        <div className="breakdown-line">
                          <span>Paying today:</span>
                          <span className="amount-highlight">${50.00}</span>
                        </div>
                        <div className="breakdown-line">
                          <span>Remaining balance (pay at delivery):</span>
                          <span>${getRemainingAmount().toFixed(2)}</span>
                        </div>
                      </div>
                    )}
                  </div>
                </label>
              </div>
              
              <div className={`payment-option ${paymentOption === 'full' ? 'selected' : ''}`}>
                <label>
                  <input
                    type="radio"
                    name="paymentOption"
                    value="full"
                    checked={paymentOption === 'full'}
                    onChange={() => handlePaymentOptionChange('full')}
                  />
                  <div className="payment-option-content">
                    <div className="payment-option-header">
                      <h4>{appliedCoupon && appliedCoupon.isTesting ? 'Test Payment' : 'Pay Full Amount'}</h4>
                      <span className="payment-amount">${appliedCoupon && appliedCoupon.isTesting ? '1.00' : calculateFullTotal().toFixed(2)}</span>
                    </div>
                    <p className="payment-description">
                      {appliedCoupon && appliedCoupon.isTesting ? 
                        'Process a $1.00 test payment to validate the checkout system.' :
                        'Pay the complete amount today and you\'re all set. No additional payment required at delivery.'
                      }
                    </p>
                    {paymentOption === 'full' && (
                      <div className="payment-breakdown">
                        <div className="breakdown-line">
                          <span>Paying today:</span>
                          <span className="amount-highlight">${appliedCoupon && appliedCoupon.isTesting ? '1.00' : calculateFullTotal().toFixed(2)}</span>
                        </div>
                        <div className="breakdown-line">
                          <span>Remaining balance:</span>
                          <span>$0.00</span>
                        </div>
                      </div>
                    )}
                  </div>
                </label>
              </div>
            </div>
          </div>
        </div>
        
        <div className="order-totals review-totals">
          <div className="order-subtotal">
            <span>Subtotal</span>
            <span>${cart.total.toFixed(2)}</span>
          </div>
          {liabilityWaiver.purchaseDamageWaiver && (
            <div className="order-damage-waiver">
              <span>Damage Waiver (10%)</span>
              <span>${(cart.total * 0.1).toFixed(2)}</span>
            </div>
          )}
          {liabilityWaiver.overnightUpcharge && (
            <div className="order-overnight-upcharge">
              <span>Overnight Upcharge</span>
              <span>${(100).toFixed(2)}</span>
            </div>
          )}
                            {feeCalculated && (
                    <div className="order-delivery">
                      <span>
                        Delivery Fee - {deliveryInfo.city || 'Selected Location'}
                        {(() => {
                          const area = serviceAreas.find(area => 
                            area.city.toLowerCase() === (deliveryInfo.city || '').toLowerCase()
                          );
                          if (area && area.calculation) {
                            return ` (${area.calculation.distance} mi)`;
                          }
                          return '';
                        })()}
                      </span>
                      <span>${deliveryFee.toFixed(2)}</span>
                    </div>
                  )}
          <div className="order-tax">
            <span>Tax (7%)</span>
            <span>${(cart.total * 0.07).toFixed(2)}</span>
          </div>
          {tipAmount > 0 && (
            <div className="order-tip">
              <span>Tip</span>
              <span>${tipAmount.toFixed(2)}</span>
            </div>
          )}
          {appliedCoupon && couponDiscount > 0 && (
            <div className="order-discount">
              <span>Coupon Discount ({appliedCoupon.code})</span>
              <span style={{ color: '#4CAF50' }}>-${couponDiscount.toFixed(2)}</span>
            </div>
          )}
          <div className="order-total">
            <span>Order Total</span>
            <span>${Math.max(0, (
              cart.total + 
              (feeCalculated ? deliveryFee : 0) + 
              (cart.total * 0.07) + 
              (liabilityWaiver.purchaseDamageWaiver ? cart.total * 0.1 : 0) +
              (liabilityWaiver.overnightUpcharge ? 100 : 0) +
              tipAmount -
              couponDiscount
            )).toFixed(2)}</span>
          </div>
          
          {/* Payment Summary Section - Only show on final step */}
          {activeStep === 5 && (
            <div className="payment-summary-sidebar">
              <div className="payment-today">
                <span>Charging Today</span>
                <span className="charge-amount">${getPaymentAmount().toFixed(2)}</span>
              </div>
              {getRemainingAmount() > 0 && (
                <div className="payment-remaining">
                  <span>Due at Delivery</span>
                  <span>${getRemainingAmount().toFixed(2)}</span>
                </div>
              )}
              {appliedCoupon && appliedCoupon.isTesting && (
                <div className="test-coupon-notice-sidebar">
                  <span>🧪 Test Coupon - Only $1.00 charged</span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };
  
  const renderOrderConfirmation = () => {
    return (
      <div className="order-confirmation">
        <div className="confirmation-icon">
          <FontAwesomeIcon icon={faCheck} />
        </div>
        <h1>Thank You for Your Order!</h1>
        <p className="confirmation-message">
          Your order has been successfully placed. A confirmation email has been sent to {customerInfo.email}.
        </p>
        
        <div className="confirmation-details">
          <h3>Order Details</h3>
          <p className="order-id">Order #: {orderId}</p>
          <p className="order-date">Date: {new Date().toLocaleDateString()}</p>
        </div>
        
        <div className="delivery-details">
          <h3>Delivery Information</h3>
          <p>{customerInfo.firstName} {customerInfo.lastName}</p>
          <p>{deliveryInfo.address}</p>
          <p>{deliveryInfo.city}, {deliveryInfo.state} {deliveryInfo.zipCode}</p>
          <p>Delivery Date: {formatDateSafe(deliveryInfo.deliveryDate)}</p>
          <p>Delivery Time: {deliveryInfo.deliveryTime}</p>
          {feeCalculated && deliveryFee > 0 && (
            <p>Delivery Fee: ${deliveryFee.toFixed(2)} for {deliveryInfo.city}</p>
          )}
        </div>
        
        <div className="order-summary confirmation">
          <h3>Order Summary</h3>
          <div className="order-items">
            {cart.items.map(item => (
              <div key={`${item.id}-${item.date}`} className="order-item">
                <div className="item-image">
                  {item.mainImage ? (
                    <img src={item.mainImage} alt={item.name} />
                  ) : (
                    <PlaceholderImage alt={item.name} />
                  )}
                </div>
                <div className="item-details">
                  <h4>{item.name}</h4>
                  <p className="item-quantity">Quantity: {item.quantity}</p>
                  <p className="item-rental-date">Rental Date: {new Date(item.date).toLocaleDateString()}</p>
                </div>
                <div className="item-price">${item.priceNumeric}</div>
              </div>
            ))}
          </div>
          
          <div className="order-totals">
            <div className="order-subtotal">
              <span>Subtotal</span>
              <span>${cart.total.toFixed(2)}</span>
            </div>
            {liabilityWaiver.purchaseDamageWaiver && (
              <div className="order-damage-waiver">
                <span>Damage Waiver (10%)</span>
                <span>${(cart.total * 0.1).toFixed(2)}</span>
              </div>
            )}
                      {liabilityWaiver.overnightUpcharge && (
            <div className="order-overnight-upcharge">
              <span>Overnight Upcharge</span>
              <span>${(100).toFixed(2)}</span>
            </div>
          )}
          {feeCalculated && (
            <div className="order-delivery">
              <span>
                Delivery Fee - {deliveryInfo.city || 'Selected Location'}
                {(() => {
                  const area = serviceAreas.find(area => 
                    area.city.toLowerCase() === (deliveryInfo.city || '').toLowerCase()
                  );
                  if (area && area.calculation) {
                    return ` (${area.calculation.distance} mi)`;
                  }
                  return '';
                })()}
              </span>
              <span>${deliveryFee.toFixed(2)}</span>
            </div>
          )}
          <div className="order-tax">
              <span>Tax (7%)</span>
              <span>${(cart.total * 0.07).toFixed(2)}</span>
            </div>
            {tipAmount > 0 && (
              <div className="order-tip">
                <span>Tip</span>
                <span>${tipAmount.toFixed(2)}</span>
              </div>
            )}
            {appliedCoupon && couponDiscount > 0 && (
              <div className="order-discount">
                <span>Coupon Discount ({appliedCoupon.code})</span>
                <span style={{ color: '#4CAF50' }}>-${couponDiscount.toFixed(2)}</span>
              </div>
            )}
            <div className="order-total">
              <span>Total</span>
              <span>${Math.max(0, (
                cart.total + 
                (feeCalculated ? deliveryFee : 0) + 
                (cart.total * 0.07) + 
                (liabilityWaiver.purchaseDamageWaiver ? cart.total * 0.1 : 0) +
                (liabilityWaiver.overnightUpcharge ? 100 : 0) +
                tipAmount -
                couponDiscount
              )).toFixed(2)}</span>
            </div>
            
            {/* Payment Summary Section - Only show on final step */}
            {activeStep === 5 && (
              <div className="payment-summary-sidebar">
                <div className="payment-today">
                  <span>Charging Today</span>
                  <span className="charge-amount">${getPaymentAmount().toFixed(2)}</span>
                </div>
                {getRemainingAmount() > 0 && (
                  <div className="payment-remaining">
                    <span>Due at Delivery</span>
                    <span>${getRemainingAmount().toFixed(2)}</span>
                  </div>
                )}
                {appliedCoupon && appliedCoupon.isTesting && (
                  <div className="test-coupon-notice-sidebar">
                    <span>🧪 Test Coupon - Only $1.00 charged</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderNavigationButtons = () => {
    // Only show navigation buttons if not on payment step (step 4)
    if (activeStep === 4) {
      return null;
    }
    return (
      <div className="checkout-navigation">
        {activeStep > 1 && (
          <button onClick={goToPrevStep} className="prev-button" disabled={isProcessing}>
            Back
          </button>
        )}
        
        {activeStep < 5 ? (
          <button
            onClick={goToNextStep}
            className="next-button"
            disabled={isProcessing || !!orderingPause}
          >
            Continue <FontAwesomeIcon icon={faChevronRight} />
          </button>
        ) : (
          <button
            onClick={handlePlaceOrder}
            className={`place-order-button ${orderingPause ? 'ordering-disabled' : ''}`}
            disabled={isProcessing || !!orderingPause}
          >
            {orderingPause
              ? 'Ordering Paused'
              : isProcessing
              ? 'Processing...'
              : 'Place Order'}
            {!isProcessing && !orderingPause && <FontAwesomeIcon icon={faCheck} />}
          </button>
        )}
      </div>
    );
  };

  useEffect(() => {
    // Load saved payment method from session storage on component mount
    try {
      const savedPaymentMethod = sessionStorage.getItem('roweBros_paymentMethod');
      if (savedPaymentMethod) {
        setPaymentMethod(JSON.parse(savedPaymentMethod));
        console.log('[CheckoutPage] Loaded saved payment method from session storage:', savedPaymentMethod);
      }
    } catch (error) {
      console.error('[CheckoutPage] Error loading saved payment method:', error);
    }
  }, []);

  return (
    <div className="checkout-page">
      <div className="container">
        {orderComplete ? (
          renderOrderConfirmation()
        ) : (
          <>
            {orderingPause && (
              <div className="ordering-paused-banner blocking" role="alert">
                <FontAwesomeIcon icon={faExclamationTriangle} />
                <div>
                  <strong>
                    {orderingPause.area
                      ? `We've paused new online orders for our ${orderingPause.area} location.`
                      : "We've paused new online orders."}
                  </strong>
                  <span>{orderingPause.message}</span>
                  <span>
                    Your cart is saved — you'll be able to check out as soon as
                    we're back online.
                  </span>
                </div>
              </div>
            )}

            {isGuest && (
              <div className="guest-checkout-banner">
                <p>
                  You're checking out as a guest. 
                  <button onClick={() => navigate('/login?redirect=checkout')} className="guest-login-link">
                    Login for a faster experience
                  </button>
                </p>
              </div>
            )}
            
            <div className="checkout-progress">
              <div className={`step ${activeStep >= 1 ? 'active' : ''} ${activeStep > 1 ? 'completed' : ''}`}>
                <div className="step-number">{activeStep > 1 ? '' : '1'}</div>
                <div className="step-label">Customer Info</div>
              </div>
              <div className="step-connector"></div>
              <div className={`step ${activeStep >= 2 ? 'active' : ''} ${activeStep > 2 ? 'completed' : ''}`}>
                <div className="step-number">{activeStep > 2 ? '' : '2'}</div>
                <div className="step-label">Delivery</div>
              </div>
              <div className="step-connector"></div>
              <div className={`step ${activeStep >= 3 ? 'active' : ''} ${activeStep > 3 ? 'completed' : ''}`}>
                <div className="step-number">{activeStep > 3 ? '' : '3'}</div>
                <div className="step-label">Waiver and Setup</div>
              </div>
              <div className="step-connector"></div>
              <div className={`step ${activeStep >= 4 ? 'active' : ''} ${activeStep > 4 ? 'completed' : ''}`}>
                <div className="step-number">{activeStep > 4 ? '' : '4'}</div>
                <div className="step-label">Payment</div>
              </div>
              <div className="step-connector"></div>
              <div className={`step ${activeStep >= 5 ? 'active' : ''}`}>
                <div className="step-number">5</div>
                <div className="step-label">Review</div>
              </div>
            </div>
            
            <div className="checkout-content">
              <div className="checkout-form-container">
                {renderStepContent()}
              </div>
              
              <div className="order-summary">
                <h3>Order Summary</h3>
                <div className="order-items">
                  {cart.items.map(item => (
                    <div key={`${item.id}-${item.date}`} className="order-item">
                      <div className="item-image">
                        {item.mainImage ? (
                          <img src={item.mainImage} alt={item.name} />
                        ) : (
                          <PlaceholderImage alt={item.name} />
                        )}
                      </div>
                      <div className="item-details">
                        <h4>{item.name}</h4>
                        <p className="item-quantity">Quantity: {item.quantity}</p>
                        <p className="item-rental-date">Rental Date: {new Date(item.date).toLocaleDateString()}</p>
                      </div>
                      <div className="item-price">${item.priceNumeric}</div>
                    </div>
                  ))}
                </div>
                
                {/* Coupon Section in Order Summary */}
                <div className="coupon-section-sidebar">
                  <h4>
                    <FontAwesomeIcon icon={faTag} style={{ marginRight: '0.5rem' }} />
                    Have a Coupon?
                  </h4>
                  {!appliedCoupon ? (
                    <div className="coupon-input-section">
                      <div className="coupon-input-group">
                        <input
                          type="text"
                          value={couponCode}
                          onChange={handleCouponCodeChange}
                          placeholder="Enter code"
                          className="coupon-input"
                          maxLength={20}
                        />
                        <button
                          type="button"
                          onClick={applyCouponCode}
                          disabled={couponLoading || !couponCode.trim()}
                          className="apply-coupon-btn"
                        >
                          {couponLoading ? 'Applying...' : 'Apply'}
                        </button>
                      </div>
                      {couponError && (
                        <div className="coupon-error">
                          <FontAwesomeIcon icon={faTimes} style={{ marginRight: '0.5rem' }} />
                          {couponError}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="coupon-applied">
                      <div className="coupon-success">
                        <FontAwesomeIcon icon={faCheck} style={{ marginRight: '0.5rem', color: '#4CAF50' }} />
                        <span>"{appliedCoupon.code}" applied!</span>
                      </div>
                      <div className="coupon-details">
                        <p><strong>Description:</strong> {appliedCoupon.description}</p>
                        <p><strong>Discount:</strong> {formatCouponValue(appliedCoupon)} (-${couponDiscount.toFixed(2)})</p>
                      </div>
                      <button
                        type="button"
                        onClick={removeCoupon}
                        className="remove-coupon-btn"
                      >
                        <FontAwesomeIcon icon={faTimes} style={{ marginRight: '0.5rem' }} />
                        Remove Coupon
                      </button>
                    </div>
                  )}
                </div>
                
                <div className="order-totals">
                  <div className="order-subtotal">
                    <span>Subtotal</span>
                    <span>${cart.total.toFixed(2)}</span>
                  </div>
                  {liabilityWaiver.purchaseDamageWaiver && (
                    <div className="order-damage-waiver">
                      <span>Damage Waiver (10%)</span>
                      <span>${(cart.total * 0.1).toFixed(2)}</span>
                    </div>
                  )}
                  {feeCalculated && (
                    <div className="order-delivery">
                      <span>
                        Delivery Fee - {deliveryInfo.city || 'Selected Location'}
                        {(() => {
                          const area = serviceAreas.find(area => 
                            area.city.toLowerCase() === (deliveryInfo.city || '').toLowerCase()
                          );
                          if (area && area.calculation) {
                            return ` (${area.calculation.distance} mi)`;
                          }
                          return '';
                        })()}
                      </span>
                      <span>${deliveryFee.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="order-tax">
                    <span>Tax (7%)</span>
                    <span>${(cart.total * 0.07).toFixed(2)}</span>
                  </div>
                  {tipAmount > 0 && (
                    <div className="order-tip">
                      <span>Tip</span>
                      <span>${tipAmount.toFixed(2)}</span>
                    </div>
                  )}
                  {appliedCoupon && couponDiscount > 0 && (
                    <div className="order-discount">
                      <span>Coupon Discount ({appliedCoupon.code})</span>
                      <span style={{ color: '#4CAF50' }}>-${couponDiscount.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="order-total">
                    <span>Total</span>
                    <span>${Math.max(0, (
                      cart.total + 
                      (feeCalculated ? deliveryFee : 0) + 
                      (cart.total * 0.07) + 
                      (liabilityWaiver.purchaseDamageWaiver ? cart.total * 0.1 : 0) +
                      tipAmount -
                      couponDiscount
                    )).toFixed(2)}</span>
                  </div>
                  
                  {/* Payment Summary Section - Only show on final step */}
                  {activeStep === 5 && (
                    <div className="payment-summary-sidebar">
                      <div className="payment-today">
                        <span>Charging Today</span>
                        <span className="charge-amount">${getPaymentAmount().toFixed(2)}</span>
                      </div>
                      {getRemainingAmount() > 0 && (
                        <div className="payment-remaining">
                          <span>Due at Delivery</span>
                          <span>${getRemainingAmount().toFixed(2)}</span>
                        </div>
                      )}
                      {appliedCoupon && appliedCoupon.isTesting && (
                        <div className="test-coupon-notice-sidebar">
                          <span>🧪 Test Coupon - Only $1.00 charged</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {paymentError && (
              <div className="payment-error">
                <FontAwesomeIcon icon={faTimes} /> {paymentError}
              </div>
            )}
            
            {renderNavigationButtons()}
          </>
        )}
      </div>
      
      {/* Add the Terms Modal */}
      {showTermsModal && (
        <TermsModal onClose={() => setShowTermsModal(false)} />
      )}
      
      {/* Generator Upsell Modal */}
      {showGeneratorUpsell && (
        <div className="generator-upsell-modal">
          <div className="generator-upsell-backdrop" onClick={() => setShowGeneratorUpsell(false)}></div>
          <div className="generator-upsell-content">
            <div className="generator-upsell-header">
              <h3>⚡ Power Required!</h3>
              <button 
                className="close-generator-modal"
                onClick={() => setShowGeneratorUpsell(false)}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            
            <div className="generator-upsell-body">
              <div className="generator-image">
                <img src="/images/generator.png" alt="Generator Rental" />
              </div>
              
              <div className="generator-info">
                <div className="alert-message">
                  <p><strong>No electrical outlet within 100 feet?</strong></p>
                  <p>Don't worry! Our bounce houses require power to operate the blowers that keep them inflated.</p>
                  <p>Since you don't have an outlet within 100 feet, you'll need a generator to power your rental.</p>
                </div>
                
                <div className="generator-details">
                  <h4>Generator Rental - $125/day</h4>
                  <ul>
                    <li>✓ Powers up to 2 bounce houses</li>
                    <li>✓ Quiet operation</li>
                    <li>✓ Includes fuel</li>
                    <li>✓ Professional setup included</li>
                  </ul>
                </div>
                
                <div className="generator-actions">
                  <button 
                    className="btn btn-primary add-generator"
                    onClick={() => handleGeneratorUpsell(true)}
                  >
                    <FontAwesomeIcon icon={faCheck} /> Yes, Add Generator ($125)
                  </button>
                  <button 
                    className="btn btn-secondary no-generator"
                    onClick={() => handleGeneratorUpsell(false)}
                  >
                    No, I'll arrange my own power
                  </button>
                </div>
                
                <div className="power-note">
                  <p><em>Note: If you choose not to rent our generator, please ensure you have adequate power available. Bounce houses require continuous power to operate safely.</em></p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CheckoutPage;
